import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuthContext } from './AuthContext';
import {
  ORGANIZATION_PROFILE_UPDATED,
  persistOrganizationProfile,
} from '@saas-foundation/shared-frontend/utils/organizationContext';

const OrganizationContext = createContext({});

export const useOrganizationContext = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganizationContext must be used within an OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuthContext();
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationName, setOrganizationName] = useState(null);
  const [organizationRcmSettings, setOrganizationRcmSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const getApiBaseUrl = () => {
    if (typeof window !== 'undefined' && window.location) {
      const port = window.location.port;
      const hostname = window.location.hostname;

      if (hostname === 'localhost' && (port === '3000' || port === '3001')) {
        return 'http://localhost:8001/api/v1';
      }

      return '/api/v1';
    }

    return '/api/v1';
  };

  const applyOrganization = useCallback((org, { persist = true } = {}) => {
    if (!org?.id) {
      return;
    }
    const orgId = String(org.id);
    setOrganizationId(orgId);
    if (org.name) {
      setOrganizationName(org.name);
    }
    if (persist) {
      persistOrganizationProfile(org);
    }
    const rcm = org?.settings?.rcm;
    if (rcm && typeof rcm === 'object') {
      setOrganizationRcmSettings(rcm);
      try {
        sessionStorage.setItem('organization_rcm_settings', JSON.stringify(rcm));
      } catch {
        /* ignore */
      }
    }
  }, []);

  const fetchCurrentOrganization = useCallback(async (token, orgIdHint) => {
    const API_BASE_URL = getApiBaseUrl();
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };

    const requestCurrent = async (hint) => {
      const query = hint
        ? `?organization_id=${encodeURIComponent(String(hint))}`
        : '';
      return fetch(`${API_BASE_URL}/organizations/current${query}`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
    };

    const parseOrganization = async (response) => {
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      const org = data.organization || data.data?.organization;
      if (org?.id && org?.name && org.name !== 'No Organization') {
        applyOrganization(org);
        return org;
      }
      return null;
    };

    let response = await requestCurrent(orgIdHint);
    let org = await parseOrganization(response);

    if (!org && orgIdHint) {
      localStorage.removeItem('selectedOrganizationId');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('organizationName');
      response = await requestCurrent(null);
      org = await parseOrganization(response);
    }

    if (!org) {
      const listResponse = await fetch(`${API_BASE_URL}/organizations/`, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (listResponse.ok) {
        const organizations = await listResponse.json();
        const preferred =
          (orgIdHint &&
            organizations.find((item) => String(item.id) === String(orgIdHint))) ||
          organizations[0];
        if (preferred?.id) {
          applyOrganization(preferred);
          return preferred;
        }
      }
    }

    return org;
  }, [applyOrganization]);

  useEffect(() => {
    const loadOrganization = async () => {
      if (!isAuthenticated || !user) {
        setOrganizationId(null);
        setOrganizationName(null);
        setOrganizationRcmSettings(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const orgIdHint =
          user?.organization_id ||
          user?.organizationId ||
          localStorage.getItem('selectedOrganizationId') ||
          localStorage.getItem('organizationId');

        const cachedName = localStorage.getItem('organizationName');
        if (cachedName) {
          setOrganizationName(cachedName);
        }
        if (orgIdHint) {
          setOrganizationId(String(orgIdHint));
        }

        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        if (!token) {
          setOrganizationId(null);
          setOrganizationName(null);
          return;
        }

        const resolvedOrg = await fetchCurrentOrganization(token, orgIdHint);
        if (!resolvedOrg) {
          setOrganizationId(null);
          setOrganizationName(null);
          setOrganizationRcmSettings(null);
          localStorage.removeItem('selectedOrganizationId');
          localStorage.removeItem('organizationId');
          localStorage.removeItem('organizationName');
        }
      } catch (error) {
        console.error('Error loading organization:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOrganization();
  }, [user, isAuthenticated, fetchCurrentOrganization]);

  useEffect(() => {
    const onProfileUpdated = (event) => {
      const org = event?.detail?.organization;
      if (org?.id) {
        // Event already came from persistOrganizationProfile — update React state only.
        applyOrganization(org, { persist: false });
      }
    };
    window.addEventListener(ORGANIZATION_PROFILE_UPDATED, onProfileUpdated);
    return () => window.removeEventListener(ORGANIZATION_PROFILE_UPDATED, onProfileUpdated);
  }, [applyOrganization]);

  const updateOrganizationId = useCallback(
    async (newOrgId, newOrgName) => {
      if (newOrgId) {
        const orgId = String(newOrgId);
        setOrganizationId(orgId);
        localStorage.setItem('selectedOrganizationId', orgId);
        localStorage.setItem('organizationId', orgId);
        if (newOrgName) {
          setOrganizationName(newOrgName);
          persistOrganizationProfile({ id: orgId, name: newOrgName });
          return;
        }
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        if (token) {
          await fetchCurrentOrganization(token, orgId);
        }
      } else {
        setOrganizationId(null);
        setOrganizationName(null);
        setOrganizationRcmSettings(null);
        localStorage.removeItem('selectedOrganizationId');
        localStorage.removeItem('organizationId');
        localStorage.removeItem('organizationName');
        try {
          sessionStorage.removeItem('organization_rcm_settings');
        } catch {
          /* ignore */
        }
      }
    },
    [fetchCurrentOrganization]
  );

  const refreshOrganization = useCallback(async () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token || !isAuthenticated) {
      return null;
    }
    const orgIdHint =
      organizationId ||
      localStorage.getItem('selectedOrganizationId') ||
      localStorage.getItem('organizationId');
    return fetchCurrentOrganization(token, orgIdHint);
  }, [fetchCurrentOrganization, isAuthenticated, organizationId]);

  const contextValue = useMemo(
    () => ({
      organizationId,
      organizationName,
      organizationRcmSettings,
      loading,
      updateOrganizationId,
      refreshOrganization,
      isAuthenticated: isAuthenticated && !!organizationId,
    }),
    [
      organizationId,
      organizationName,
      organizationRcmSettings,
      loading,
      updateOrganizationId,
      refreshOrganization,
      isAuthenticated,
    ]
  );

  return (
    <OrganizationContext.Provider value={contextValue}>
      {children}
    </OrganizationContext.Provider>
  );
};
